import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import RegistrationInteractionEvent, {
  type RegistrationElement,
  type RegistrationMethod,
  type RegistrationPlacement
} from '~/events/RegistrationInteractionEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { log } from '~/utils/log';
import type { ModalScreen } from '~/utils/modalProperties';

export type RegistrationEntry = {
  placement: RegistrationPlacement;
  productId?: string;
};

const RegistrationEntryContext = createContext<RegistrationEntry | null>(null);

export function RegistrationEntryProvider({
  children,
  entry
}: {
  children: ReactNode;
  entry: RegistrationEntry;
}) {
  return (
    <RegistrationEntryContext.Provider value={entry}>
      {children}
    </RegistrationEntryContext.Provider>
  );
}

type RegistrationTap = {
  element?: RegistrationElement;
  method?: RegistrationMethod;
  modal?: ModalScreen;
};

function useSendRegistration() {
  const { isBot } = useRootData();
  const user = useUser();

  return (
    entry: RegistrationEntry,
    { element, method, modal }: RegistrationTap = {}
  ) => {
    if (user.isLoggedIn) return;

    return new RegistrationInteractionEvent({
      element,
      gesture: 'click',
      guestId: user.guestId,
      isBot,
      method,
      modal,
      placement: entry.placement,
      productId: entry.productId
    }).send();
  };
}

export function useRegistrationStart() {
  const send = useSendRegistration();

  return (entry: RegistrationEntry) => send(entry);
}

export function useRegistrationMethod() {
  const entry = useContext(RegistrationEntryContext);
  const send = useSendRegistration();

  return (method: RegistrationMethod) => {
    if (!entry) {
      log.error('Registration method picked outside a sign-in prompt', {
        method
      });

      return;
    }

    return send(entry, { method });
  };
}

export function useRegistrationSetPassword(screen: ModalScreen) {
  const send = useSendRegistration();

  return () =>
    send(
      { placement: 'email_confirmation' },
      { element: 'set_password', modal: screen }
    );
}
